import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid, TextField, 
  Switch, FormControlLabel, Alert, CircularProgress, Select, MenuItem, 
  FormControl, InputLabel, Divider, Chip, IconButton, Tooltip,
  Snackbar, useTheme, alpha
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  NetworkCheck as NetworkCheckIcon
} from '@mui/icons-material';
import axios from '../config/axios';
import { API_CONFIG } from '../config/api';

interface WhatsAppInstance {
  id: string;
  name: string;
  instance_name: string;
  is_connected: boolean;
  last_activity: string;
  proxy_config?: {
    enabled: boolean;
    protocol?: string;
    host?: string;
    port?: number;
    username?: string;
    password?: string;
  };
}

interface ProxyConfig {
  enabled: boolean;
  protocol: string;
  host: string;
  port: string;
  username: string;
  password: string;
}

const WhatsAppProxyConfig: React.FC = () => {
  const theme = useTheme();
  
  const [instances, setInstances] = useState<WhatsAppInstance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [proxyConfig, setProxyConfig] = useState<ProxyConfig>({
    enabled: false,
    protocol: 'http',
    host: '',
    port: '',
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [instancesLoading, setInstancesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' } | null>(null);

  useEffect(() => {
    fetchInstances();
  }, []);

  useEffect(() => {
    if (selectedInstance) {
      fetchProxyConfig(selectedInstance);
    }
  }, [selectedInstance]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(null);
  };

  const fetchInstances = async () => {
    try {
      setInstancesLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_CONFIG.BASE_URL}/whatsapp-proxy/instances`);
      
      if (response.data.success) {
        setInstances(response.data.data);
        if (response.data.data.length > 0 && !selectedInstance) {
          setSelectedInstance(response.data.data[0].instance_name);
        }
      } else {
        throw new Error(response.data.error || 'Erro ao buscar instâncias');
      }
    } catch (err: any) {
      console.error('Erro ao buscar instâncias:', err);
      setError('Erro ao carregar instâncias WhatsApp');
      showSnackbar('Erro ao carregar instâncias WhatsApp', 'error');
    } finally {
      setInstancesLoading(false);
    }
  };

  const fetchProxyConfig = async (instanceName: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_CONFIG.BASE_URL}/whatsapp-proxy/config/${instanceName}`);
      
      if (response.data.success) {
        const config = response.data.data;
        setProxyConfig({
          enabled: config.enabled || false,
          protocol: config.protocol || 'http',
          host: config.host || '',
          port: config.port ? config.port.toString() : '',
          username: config.username || '',
          password: config.password || ''
        });
      } else {
        // Se não há configuração, usar valores padrão
        setProxyConfig({
          enabled: false,
          protocol: 'http',
          host: '',
          port: '',
          username: '',
          password: ''
        });
      }
    } catch (err: any) {
      console.error('Erro ao buscar configuração de proxy:', err);
      // Em caso de erro, usar valores padrão
      setProxyConfig({
        enabled: false,
        protocol: 'http',
        host: '',
        port: '',
        username: '',
        password: ''
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedInstance) {
      setError('Selecione uma instância');
      return;
    }

    if (proxyConfig.enabled && (!proxyConfig.host || !proxyConfig.port)) {
      setError('Host e Porta são obrigatórios quando o proxy está habilitado');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payload = {
        enabled: proxyConfig.enabled,
        ...(proxyConfig.enabled && {
          protocol: proxyConfig.protocol,
          host: proxyConfig.host,
          port: parseInt(proxyConfig.port),
          ...(proxyConfig.username && { username: proxyConfig.username }),
          ...(proxyConfig.password && { password: proxyConfig.password })
        })
      };

      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/whatsapp-proxy/config/${selectedInstance}`,
        payload
      );

      if (response.data.success) {
        showSnackbar('Configuração de proxy salva com sucesso!', 'success');
        // Atualizar a lista de instâncias para refletir as mudanças
        fetchInstances();
      } else {
        throw new Error(response.data.error || 'Erro ao salvar configuração');
      }
    } catch (err: any) {
      console.error('Erro ao salvar configuração:', err);
      setError(err.response?.data?.error || 'Erro ao salvar configuração de proxy');
      showSnackbar(err.response?.data?.error || 'Erro ao salvar configuração de proxy', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!selectedInstance) {
      setError('Selecione uma instância');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.delete(`${API_CONFIG.BASE_URL}/whatsapp-proxy/config/${selectedInstance}`);

      if (response.data.success) {
        showSnackbar('Proxy desabilitado com sucesso!', 'success');
        setProxyConfig(prev => ({ ...prev, enabled: false }));
        fetchInstances();
      } else {
        throw new Error(response.data.error || 'Erro ao desabilitar proxy');
      }
    } catch (err: any) {
      console.error('Erro ao desabilitar proxy:', err);
      setError(err.response?.data?.error || 'Erro ao desabilitar proxy');
      showSnackbar(err.response?.data?.error || 'Erro ao desabilitar proxy', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInstanceChange = (instanceName: string) => {
    setSelectedInstance(instanceName);
  };

  const handleConfigChange = (field: keyof ProxyConfig, value: any) => {
    setProxyConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getInstanceStatus = (instance: WhatsAppInstance) => {
    return instance.is_connected ? (
      <Chip 
        icon={<CheckCircleIcon />} 
        label="Conectado" 
        color="success" 
        size="small" 
      />
    ) : (
      <Chip 
        icon={<ErrorIcon />} 
        label="Desconectado" 
        color="error" 
        size="small" 
      />
    );
  };

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      backgroundColor: 'transparent', 
      minHeight: '100vh',
      color: 'text.primary',
      position: 'relative',
      zIndex: 1,
    }}>

      {/* Header com título */}
      <Box sx={{ 
        mb: { xs: 2, md: 3 }, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' },
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2, md: 0 },
        padding: { xs: '16px', md: '20px 24px' },
        position: 'relative',
        zIndex: 1,
      }}>
        <Box>
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold', 
            color: '#000000',
            fontSize: { xs: '1.5rem', md: '2.125rem' },
            mb: 0.5,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <SettingsIcon sx={{ fontSize: { xs: 24, md: 32 }, color: '#3b82f6' }} />
            Configurar Proxy WhatsApp
          </Typography>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary',
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            Configure o proxy para as instâncias WhatsApp conectadas à Evolution API
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Seleção de Instância */}
        <Grid item xs={12} md={6}>
          <Card className="glass-blue-card" sx={{
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#000000', fontWeight: 600 }}>
                <SecurityIcon sx={{ mr: 1, color: '#3b82f6' }} />
                Instância WhatsApp
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#475569' }}>Selecionar Instância</InputLabel>
                  <Select
                    value={selectedInstance}
                    onChange={(e) => handleInstanceChange(e.target.value)}
                    disabled={instancesLoading}
                    sx={{
                      color: '#000000',
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(59, 130, 246, 0.08)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: '1px solid rgba(59, 130, 246, 0.2)',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: 'rgba(59, 130, 246, 0.4)',
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                        },
                        '&.Mui-focused': {
                          borderColor: 'rgba(59, 130, 246, 0.6)',
                          boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                        },
                        '& fieldset': {
                          border: 'none',
                        },
                      },
                      '& .MuiSelect-icon': {
                        color: '#64748b',
                      },
                    }}
                  >
                    {instances.map((instance) => (
                      <MenuItem key={instance.id} value={instance.instance_name}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{instance.name}</Typography>
                          {getInstanceStatus(instance)}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Tooltip title="Atualizar lista de instâncias">
                  <IconButton 
                    onClick={fetchInstances} 
                    disabled={instancesLoading}
                    sx={{
                      color: '#000000',
                      '&:hover': {
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        color: '#3b82f6',
                      }
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {selectedInstance && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ color: '#475569', mb: 1 }}>
                    <strong>Instância:</strong> {selectedInstance}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569' }}>
                    <strong>Última atividade:</strong> {
                      instances.find(i => i.instance_name === selectedInstance)?.last_activity 
                        ? new Date(instances.find(i => i.instance_name === selectedInstance)!.last_activity).toLocaleString()
                        : 'N/A'
                    }
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Configuração de Proxy */}
        <Grid item xs={12} md={6}>
          <Card className="glass-blue-card" sx={{
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#000000', fontWeight: 600 }}>
                <NetworkCheckIcon sx={{ mr: 1, color: '#3b82f6' }} />
                Configuração de Proxy
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={proxyConfig.enabled}
                    onChange={(e) => handleConfigChange('enabled', e.target.checked)}
                    disabled={loading}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#3b82f6',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#3b82f6',
                      },
                    }}
                  />
                }
                label={<Typography sx={{ color: '#475569' }}>Habilitar Proxy</Typography>}
                sx={{ mb: 2 }}
              />

              {proxyConfig.enabled && (
                <>
                  <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: '#475569' }}>Protocolo</InputLabel>
                        <Select
                          value={proxyConfig.protocol}
                          onChange={(e) => handleConfigChange('protocol', e.target.value)}
                          disabled={loading}
                          sx={{
                            color: '#000000',
                            '& .MuiOutlinedInput-root': {
                              background: 'rgba(59, 130, 246, 0.08)',
                              backdropFilter: 'blur(10px)',
                              WebkitBackdropFilter: 'blur(10px)',
                              border: '1px solid rgba(59, 130, 246, 0.2)',
                              borderRadius: '8px',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: 'rgba(59, 130, 246, 0.4)',
                                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                              },
                              '&.Mui-focused': {
                                borderColor: 'rgba(59, 130, 246, 0.6)',
                                boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                              },
                              '& fieldset': {
                                border: 'none',
                              },
                            },
                            '& .MuiSelect-icon': {
                              color: '#64748b',
                            },
                          }}
                        >
                          <MenuItem value="http">HTTP</MenuItem>
                          <MenuItem value="https">HTTPS</MenuItem>
                          <MenuItem value="socks4">SOCKS4</MenuItem>
                          <MenuItem value="socks5">SOCKS5</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Porta"
                        type="number"
                        value={proxyConfig.port}
                        onChange={(e) => handleConfigChange('port', e.target.value)}
                        disabled={loading}
                        placeholder="8080"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: 'rgba(59, 130, 246, 0.08)',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                            border: '1px solid rgba(59, 130, 246, 0.2)',
                            borderRadius: '8px',
                            color: '#000000',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: 'rgba(59, 130, 246, 0.4)',
                              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                            },
                            '&.Mui-focused': {
                              borderColor: 'rgba(59, 130, 246, 0.6)',
                              boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                            },
                            '& fieldset': {
                              border: 'none',
                            },
                            '& input': {
                              color: '#000000',
                              '&::placeholder': {
                                color: '#94a3b8',
                                opacity: 1,
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#64748b',
                            '&.Mui-focused': {
                              color: 'rgba(59, 130, 246, 0.8)',
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Host"
                        value={proxyConfig.host}
                        onChange={(e) => handleConfigChange('host', e.target.value)}
                        disabled={loading}
                        placeholder="proxy.exemplo.com"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: 'rgba(59, 130, 246, 0.08)',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                            border: '1px solid rgba(59, 130, 246, 0.2)',
                            borderRadius: '8px',
                            color: '#000000',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: 'rgba(59, 130, 246, 0.4)',
                              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                            },
                            '&.Mui-focused': {
                              borderColor: 'rgba(59, 130, 246, 0.6)',
                              boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                            },
                            '& fieldset': {
                              border: 'none',
                            },
                            '& input': {
                              color: '#000000',
                              '&::placeholder': {
                                color: '#94a3b8',
                                opacity: 1,
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#64748b',
                            '&.Mui-focused': {
                              color: 'rgba(59, 130, 246, 0.8)',
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Usuário (opcional)"
                        value={proxyConfig.username}
                        onChange={(e) => handleConfigChange('username', e.target.value)}
                        disabled={loading}
                        placeholder="usuario"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: 'rgba(59, 130, 246, 0.08)',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                            border: '1px solid rgba(59, 130, 246, 0.2)',
                            borderRadius: '8px',
                            color: '#000000',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: 'rgba(59, 130, 246, 0.4)',
                              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                            },
                            '&.Mui-focused': {
                              borderColor: 'rgba(59, 130, 246, 0.6)',
                              boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                            },
                            '& fieldset': {
                              border: 'none',
                            },
                            '& input': {
                              color: '#000000',
                              '&::placeholder': {
                                color: '#94a3b8',
                                opacity: 1,
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#64748b',
                            '&.Mui-focused': {
                              color: 'rgba(59, 130, 246, 0.8)',
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Senha (opcional)"
                        type="password"
                        value={proxyConfig.password}
                        onChange={(e) => handleConfigChange('password', e.target.value)}
                        disabled={loading}
                        placeholder="senha"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: 'rgba(59, 130, 246, 0.08)',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                            border: '1px solid rgba(59, 130, 246, 0.2)',
                            borderRadius: '8px',
                            color: '#000000',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: 'rgba(59, 130, 246, 0.4)',
                              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                            },
                            '&.Mui-focused': {
                              borderColor: 'rgba(59, 130, 246, 0.6)',
                              boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                            },
                            '& fieldset': {
                              border: 'none',
                            },
                            '& input': {
                              color: '#000000',
                              '&::placeholder': {
                                color: '#94a3b8',
                                opacity: 1,
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#64748b',
                            '&.Mui-focused': {
                              color: 'rgba(59, 130, 246, 0.8)',
                            },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Ações */}
        <Grid item xs={12}>
          <Card className="glass-blue-card" sx={{
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={loading || !selectedInstance}
                  sx={{ 
                    minWidth: 150,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                      boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
                    },
                    '&:disabled': {
                      background: 'rgba(59, 130, 246, 0.08)',
                      color: '#94a3b8',
                    }
                  }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Salvar Configuração'}
                </Button>

                {proxyConfig.enabled && (
                  <Button
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={handleDisable}
                    disabled={loading || !selectedInstance}
                    sx={{ 
                      minWidth: 150,
                      background: 'rgba(59, 130, 246, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#000000',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        color: '#ef4444',
                      },
                      '&:disabled': {
                        background: 'rgba(255, 255, 255, 0.05)',
                        color: 'rgba(255, 255, 255, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    Desabilitar Proxy
                  </Button>
                )}

                <Tooltip title="Informações sobre configuração de proxy">
                  <IconButton sx={{
                    color: '#000000',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      color: '#3b82f6',
                    }
                  }}>
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Alert severity="info" sx={{ mt: 2, backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  <strong>Dica:</strong> O proxy será aplicado apenas para a instância selecionada. 
                  Certifique-se de que as configurações estão corretas antes de salvar.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar?.open || false}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar?.severity} sx={{ width: '100%' }}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppProxyConfig;

